import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCallout,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import { useEmployerTrainingSession } from "../../components/employer/EmployerModeContext";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildGateSummary, formatAdStatusLabel, formatDateTime } from "../../services/employer/employerCaseOsDomain";
import {
  buildEmployerTrustOpsSummary,
  buildEmployerTrustSummary,
  getPublishingRightsStageColor,
  getRiskLevelColor,
  getTrustTierColor,
} from "../../services/employer/employerTrustDomain";

const statusFilterOptions = [
  { label: "Alle", value: "all" },
  { label: "Entwurf", value: "draft" },
  { label: "Veroeffentlicht", value: "published" },
  { label: "Aktiv", value: "active" },
  { label: "Pausiert", value: "paused" },
  { label: "Archiviert", value: "archived" },
];

const actionLabels = {
  edit: "Wizard",
  candidates: "Kandidaten",
  preview: "Preview",
  training: "Training starten",
  publish: "Veroeffentlichen",
  pause: "Pausieren",
  resume: "Reaktivieren",
  archive: "Archivieren",
  delete: "Loeschen",
};

function resolveGateReadiness(ad, gateSummary) {
  const adReady = Number(ad.onboarding_bonus || 0) >= 1500 && String(ad.wohnungsunterstuetzung_art || "").trim();
  if (adReady && gateSummary.status === "complete") return "Case-ready";
  if (adReady || gateSummary.status === "review") return "Mit Gate-Risiko";
  return "Gate offen";
}

function lifecycleActionsForAd(ad) {
  const status = String(ad.status || "").toLowerCase();
  const actions = ["edit", "candidates", "preview"];
  if (status === "draft") return [...actions, "publish", "archive", "delete"];
  if (status === "active") return [...actions, "pause", "archive"];
  if (status === "paused") return [...actions, "resume", "archive"];
  if (status === "published") return [...actions, "resume", "pause", "archive"];
  return [...actions, "archive"];
}

export default function EmployerJobsPage() {
  const navigate = useNavigate();
  const workspace = useEmployerOsWorkspace();
  const { currentRun, resumeTraining, startTrainingForJobAd } = useEmployerTrainingSession();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [previewAdId, setPreviewAdId] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const gateSummary = useMemo(() => buildGateSummary(workspace.profile || {}), [workspace.profile]);
  const trustSummary = useMemo(() => buildEmployerTrustSummary(workspace.profile || {}), [workspace.profile]);
  const trustOpsSummary = useMemo(() => buildEmployerTrustOpsSummary(workspace.profile || {}), [workspace.profile]);

  const filteredAds = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (workspace.ads || [])
      .filter((entry) => statusFilter === "all" || String(entry.status || "").toLowerCase() === statusFilter)
      .filter((entry) => {
        if (!needle) return true;
        return [
          entry.titel,
          entry.berufsbezeichnung,
          entry.beruf_text,
          entry.standort_ort,
          entry.standort_land,
        ].some((value) => String(value || "").toLowerCase().includes(needle));
      });
  }, [query, statusFilter, workspace.ads]);

  const previewAd = filteredAds.find((entry) => String(entry.id) === String(previewAdId))
    || (workspace.ads || []).find((entry) => String(entry.id) === String(previewAdId))
    || null;

  const tableItems = filteredAds.map((entry) => ({
    id: entry.id,
    title: entry.titel || entry.berufsbezeichnung || "Anzeige",
    status: formatAdStatusLabel(entry.status),
    location: [entry.standort_land, entry.standort_ort].filter(Boolean).join(" / ") || "-",
    gate: resolveGateReadiness(entry, gateSummary),
    trust: trustSummary.trustStatusLabel,
    trustBlocker: trustSummary.publishBlockers[0] || "",
    updatedAt: formatDateTime(entry.updated_at || entry.created_at),
    nextAction: lifecycleActionsForAd(entry).includes("publish")
      ? (trustSummary.canPublishLive ? "Fertigstellen und veroeffentlichen" : "Trust-Blocker aufloesen")
      : lifecycleActionsForAd(entry).includes("candidates")
        ? "Kandidaten pruefen"
        : "Status steuern",
    raw: entry,
  }));

  const executeAction = async () => {
    if (!pendingAction) return;
    const { ad, action } = pendingAction;
    if (action === "edit") {
      navigate(`/dashboard/employer/jobs/wizard?id=${ad.id}`);
      setPendingAction(null);
      return;
    }
    if (action === "candidates") {
      navigate(`/dashboard/employer/candidates?adId=${ad.id}`);
      setPendingAction(null);
      return;
    }
    if (action === "preview") {
      setPreviewAdId(String(ad.id));
      setPendingAction(null);
      return;
    }
    if (action === "training") {
      await startTrainingForJobAd(ad.id, currentRun?.scenario_key || "documents_missing");
      setPendingAction(null);
      return;
    }
    if (["publish", "resume"].includes(action) && !trustSummary.canPublishLive) {
      setPendingAction(null);
      return;
    }
    await workspace.adapter.runAdAction(ad.id, action);
    setPendingAction(null);
    await workspace.loadAds();
  };

  return (
    <EmployerPageFrame
      title="Anzeigen"
      subtitle="Jobs werden jetzt als operative Bestandssurface gefuehrt: ein Wizard, eine Liste, ein Preview-Inspect und keine parallelen Editorfamilien mehr."
      actions={(
        <>
          {currentRun ? (
            <CButton color="secondary" variant="outline" onClick={resumeTraining}>
              Training fortsetzen
            </CButton>
          ) : null}
          <CButton color="secondary" variant="outline" onClick={() => workspace.loadAds()}>
            Aktualisieren
          </CButton>
          <CButton color="primary" onClick={() => navigate("/dashboard/employer/jobs/wizard")}>
            Anzeigen-Wizard
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Job OS",
        shellDescription: "Der Anzeigenbereich fuehrt ueber Bestand, Lifecycle, Kandidatenzugang und denselben einzigen Wizard.",
        topContext: "Anzeigen",
      }}
    >
      {workspace.error ? (
        <CCallout color="danger">
          <strong>Anzeigen konnten nicht vollstaendig geladen werden.</strong>
          <div>{workspace.error}</div>
        </CCallout>
      ) : null}

      <section className="dashboard-card-shell space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div>
            <CFormLabel>Suche</CFormLabel>
            <CFormInput value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Titel, Beruf, Ort ..." />
          </div>
          <div>
            <CFormLabel>Status</CFormLabel>
            <CFormSelect value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} options={statusFilterOptions} />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Jobs im Filter</span>
            <strong>{filteredAds.length}</strong>
            <p className="mb-0 text-sm text-slate-600">{workspace.ads.length} Anzeigen insgesamt im Employer OS.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Employer Gate</span>
            <strong>{gateSummary.statusLabel}</strong>
            <p className="mb-0 text-sm text-slate-600">Das Gate wird direkt gegen den Anzeigenbestand gelesen, nicht separat daneben.</p>
          </div>
        </div>

        <CCallout color={trustSummary.canPublishLive ? "success" : "warning"}>
          <strong>{trustSummary.canPublishLive ? "Trust Gate ist publishing-ready." : "Trust Gate blockiert Live-Schaltung."}</strong>
          <div>
            {trustSummary.canPublishLive
              ? `${trustSummary.employerTypeLabel} kann Anzeigen als verifizierter Direktarbeitgeber live stellen.`
              : trustSummary.publishBlockers[0] || trustSummary.nextTrustAction}
          </div>
          <div className="mt-2 d-flex flex-wrap gap-2">
            <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
            <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
            <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
          </div>
        </CCallout>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Review-Workflow</span>
            <strong>{trustOpsSummary.reviewWorkflowLabel}</strong>
            <p className="mb-0 text-sm text-slate-600">Trust wird operativ per Review-Tasks statt nur per Status gepflegt.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Blockierende Tasks</span>
            <strong>{trustOpsSummary.blockingTasks}</strong>
            <p className="mb-0 text-sm text-slate-600">{trustOpsSummary.openTasks} offene Tasks insgesamt.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Aktive Incidents</span>
            <strong>{trustOpsSummary.activeIncidents}</strong>
            <p className="mb-0 text-sm text-slate-600">
              {trustOpsSummary.criticalIncidents ? `${trustOpsSummary.criticalIncidents} kritisch` : "keine kritischen Incidents"}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Naechste Trust-Aktion</span>
            <strong>{trustOpsSummary.underInvestigation ? "Untersuchung aktiv" : "Operativ"}</strong>
            <p className="mb-0 text-sm text-slate-600">{trustOpsSummary.nextAction}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Adaptive Trust</span>
            <strong>{trustOpsSummary.employerClassificationLabel}</strong>
            <p className="mb-0 text-sm text-slate-600">
              GPT-Risiko {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% · Behavior {(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%
            </p>
          </div>
        </div>

        {tableItems.length ? (
          <CSmartTable
            columns={[
              { key: "title", label: "Anzeige" },
              { key: "status", label: "Lifecycle" },
              { key: "location", label: "Standort" },
              { key: "gate", label: "Gate / Readiness" },
              { key: "trust", label: "Trust" },
              { key: "nextAction", label: "Naechste Aktion" },
              { key: "updatedAt", label: "Aktualisiert" },
              { key: "actions", label: "Aktionen", sorter: false, filter: false },
            ]}
            items={tableItems}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              trust: (item) => (
                <td>
                  <div className="d-grid gap-1">
                    <CBadge color={trustSummary.canPublishLive ? "success" : "warning"}>{item.trust}</CBadge>
                    <div className="d-flex flex-wrap gap-1">
                      <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
                      <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
                    </div>
                    {!trustSummary.canPublishLive && item.trustBlocker ? (
                      <span className="text-xs text-slate-500">{item.trustBlocker}</span>
                    ) : null}
                  </div>
                </td>
              ),
              actions: (item) => (
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    <CButton color="secondary" size="sm" variant="outline" onClick={() => setPendingAction({ ad: item.raw, action: "edit" })}>
                      Wizard
                    </CButton>
                    <CButton color="secondary" size="sm" variant="outline" onClick={() => setPendingAction({ ad: item.raw, action: "candidates" })}>
                      Kandidaten
                    </CButton>
                    <CButton color="secondary" size="sm" variant="outline" onClick={() => setPendingAction({ ad: item.raw, action: "preview" })}>
                      Preview
                    </CButton>
                    {!currentRun ? (
                      <CButton color="secondary" size="sm" variant="outline" onClick={() => setPendingAction({ ad: item.raw, action: "training" })}>
                        Training starten
                      </CButton>
                    ) : null}
                    {lifecycleActionsForAd(item.raw)
                      .filter((action) => !["edit", "candidates", "preview"].includes(action))
                      .slice(0, 2)
                      .map((action) => (
                        <CButton
                          key={action}
                          color="primary"
                          size="sm"
                          variant="outline"
                          disabled={["publish", "resume"].includes(action) && !trustSummary.canPublishLive}
                          onClick={() => setPendingAction({ ad: item.raw, action })}
                        >
                          {actionLabels[action] || action}
                        </CButton>
                      ))}
                  </div>
                </td>
              ),
            }}
          />
        ) : (
          <CCallout color="info">
            <strong>Keine Anzeigen im aktuellen Filter.</strong>
            <div>Der Jobs-Bereich bleibt eine operative Liste. Wenn nichts passt, ist die naechste Bewegung der Wizard oder ein anderer Filter.</div>
          </CCallout>
        )}
      </section>

      <COffcanvas placement="end" visible={Boolean(previewAd)} onHide={() => setPreviewAdId("")}>
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>{previewAd?.titel || previewAd?.berufsbezeichnung || "Anzeigenpreview"}</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewAd ? (
            <div className="d-grid gap-3">
              <CCallout color="info">
                <strong>{formatAdStatusLabel(previewAd.status)}</strong>
                <div>{[previewAd.standort_land, previewAd.standort_ort].filter(Boolean).join(" / ") || "Kein Standort gepflegt."}</div>
              </CCallout>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Readiness</span>
                <strong>{resolveGateReadiness(previewAd, gateSummary)}</strong>
                <p className="mb-0 text-sm text-slate-600">Willkommensbonus {previewAd.onboarding_bonus || 0} EUR · {previewAd.wohnungsunterstuetzung_art || "Wohnunterstuetzung offen"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Beschreibung</span>
                <p className="mb-0 text-sm text-slate-700">{previewAd.beschreibung || "Keine Beschreibung gepflegt."}</p>
              </div>
              <CButton color="primary" onClick={() => navigate(`/dashboard/employer/jobs/wizard?id=${previewAd.id}&step=4`)}>
                Im Wizard oeffnen
              </CButton>
            </div>
          ) : null}
        </COffcanvasBody>
      </COffcanvas>

      <CModal visible={Boolean(pendingAction)} onClose={() => setPendingAction(null)}>
        <CModalHeader>
          <CModalTitle>Aktion bestaetigen</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {pendingAction ? (
            <>
              Aktion <strong>{actionLabels[pendingAction.action] || pendingAction.action}</strong> fuer <strong>{pendingAction.ad?.titel || pendingAction.ad?.berufsbezeichnung || `Anzeige #${pendingAction.ad?.id}`}</strong> ausfuehren.
              {["publish", "resume"].includes(pendingAction.action) && !trustSummary.canPublishLive ? (
                <div className="mt-3 d-grid gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <strong>{trustSummary.publishBlockers[0] || trustSummary.nextTrustAction}</strong>
                  <span>
                    {trustOpsSummary.trustTierLabel} · {trustOpsSummary.riskLevelLabel} · {trustOpsSummary.publishingRightsStageLabel}
                  </span>
                  <span>
                    GPT-Reality-Check: {trustOpsSummary.realityCheck.summary || "noch kein externer Check"}.
                  </span>
                  {trustOpsSummary.blockers.slice(0, 3).map((blocker) => (
                    <span key={blocker}>{blocker}</span>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setPendingAction(null)}>
            Abbrechen
          </CButton>
          <CButton color="primary" disabled={Boolean(pendingAction && ["publish", "resume"].includes(pendingAction.action) && !trustSummary.canPublishLive)} onClick={executeAction}>
            Bestaetigen
          </CButton>
        </CModalFooter>
      </CModal>
    </EmployerPageFrame>
  );
}
